"use strict";

import Cookies from "js-cookie";
import { from, of, tap } from "rxjs";
import { ajax } from "rxjs/ajax";
import { ofType } from "redux-observable";
import {
  mergeMap,
  catchError,
  map,
  concatMap,
  switchMap,
} from "rxjs/operators";
import {
  loginAuthComplete,
  logoutAuthComplete,
  clearReducer,
  ajaxError,
  hideCustomeError,
  otpResendComplete,
  expiredPasswordChangeValueComplete,
  setFirstTimeLoginState,
  keyGenerationComplete,
  payloadEncryptionSuccess,
  payloadEncryptionFail,
  regenerateKey,
  onBoardingOtpShow,
  idleTimeoutComplete,
  forgotUsernameValidateComplete,
  forgotUsernameValidateError,
  CSRFTokenGenComplete,
  CSRFTokenGenError,
  otpConfirmComplete,
  changeLanguageComplete,
  changeLanguageSuccessAlertHide,
  forgotUsernameOTPConfirmComplete,
  forgotUsernameOTPResendComplete,
  forgotUsernameOTPConfirmError,
  forgotUsernameOTPResendError,
  forgotPasswordValidateComplete,
  forgotPasswordValidateError,
  forgotPasswordOTPConfirmComplete,
  forgotPasswordOTPConfirmError,
  forgotPasswordOTPResendComplete,
  forgotPasswordOTPResendError,
  forgotPasswordChangeValueError,
  forgotPasswordAccountConfirmComplete,
  forgotPasswordChangeValueComplete,
  forgotPasswordAccountConfirmError,
  expiredPasswordChangeValueError,
  setAdminPasswordResetStatus,
  getProfileDetailsRequest,
} from "../_actions";
import {
  authConstants,
  responseCodes,
  errorResponseCodes,
  commonConstants,
} from "../_constants";
import { getRequestHeader } from "../../_helpers/request.header";
import { payloadFormatter } from "../../_helpers/encryption.helper";
import {
  setSessionActive,
  removeUserDetails,
} from "../../_helpers/auth.helper";

export const loginAuthEpic = (actions$, state$, { history }) =>
  actions$.pipe(
    ofType(authConstants.AUTH_LOGIN_REQUEST),
    mergeMap(async (action) => {
      const { setPasswordExpired, ...payloadRest } = action.payload;

      try {
        const payload = await from(
          payloadFormatter(payloadRest, "/api/auth/login"),
        ).toPromise();
        return payloadEncryptionSuccess(payload, {
          ...payloadRest,
          setPasswordExpired,
        });
      } catch (error) {
        return payloadEncryptionFail(error);
      }
    }),
    switchMap((action) => {
      return ajax
        .post(
          "/api/auth/login",
          { ...action.encryptedPayload, fingerPrint: action.payload.uid },
          getRequestHeader("POST"),
        )
        .pipe(
          tap(() => {
            setSessionActive();
            // Mark this tab as having an active session
            localStorage.setItem("ACTIVE_SESSION_TAB", Date.now().toString());
          }),
          concatMap((ajaxResponse) =>
            of(
              changeLanguageComplete({
                customerLanguage:
                  ajaxResponse.response.userDetails.customerLanguage,
              }),
              changeLanguageSuccessAlertHide(),
              getProfileDetailsRequest(),
              loginAuthComplete(
                ajaxResponse.response,
                ajaxResponse.response?.userDetails,
              ),
            ),
          ),
          catchError((error) => {
            if (
              error.response?.statusCode ==
              responseCodes.AUTH_ERROR_PASSWORD_EXPIRED
            ) {
              action.payload?.setPasswordExpired(true);
            }

            if (
              error.response?.statusCode ==
              responseCodes.AUTH_ERROR_KEY_PAIRE_EXPIRED
            ) {
              return of(
                loginAuthComplete(
                  {
                    ...error.response?.userDetails,
                    customerStatus: error.response?.statusCode,

                    isOnboardingLogin: action.payload.isOnboardingLogin,
                  },
                  error.response?.userDetails,
                ),
                regenerateKey(),
              );
            } else {
              return of(
                loginAuthComplete(
                  {
                    ...(error.response?.userDetails ||
                      error.response?.errorDetails),
                    customerStatus: error.response?.statusCode,

                    isOnboardingLogin: action.payload.isOnboardingLogin,
                  },
                  error.response?.userDetails || error.response?.errorDetails,
                ),
                ajaxError(error),
              );
            }
          }),
        );
    }),
  );

export const logoutAuthEpic = (actions$, state$, { history }) =>
  actions$.pipe(
    ofType(authConstants.AUTH_LOGOUT_REQUEST),
    mergeMap((action) => {
      return ajax
        .post("/api/auth/logout", action.payload, getRequestHeader("POST"))
        .pipe(
          tap(() => {
            removeUserDetails();
          }),
          concatMap((ajaxResponse) =>
            of(logoutAuthComplete(ajaxResponse.response), clearReducer()),
          ),
          tap(() => {
            history.push("/");
          }),

          catchError((error) => {
            removeUserDetails();

            return of(ajaxError(error));
          }),
        );
    }),
  );

export const idleTimeoutEpic = (actions$, { history }) =>
  actions$.pipe(
    ofType(authConstants.IDLE_TIMEOUT_LOGOUT_REQUEST),
    mergeMap((action) => {
      return ajax
        .post("/api/auth/idleTimeout", action.payload, getRequestHeader("POST"))
        .pipe(
          tap(() => {
            removeUserDetails();
          }),
          concatMap((ajaxResponse) =>
            of(
              idleTimeoutComplete(ajaxResponse.response),
              clearReducer(),
              ajaxError(ajaxResponse),
            ),
          ),
          tap(() => {
            history.push("/");
          }),

          catchError((error) => {
            removeUserDetails();

            return of(ajaxError(error));
          }),
        );
    }),
  );

export const otpConfirmEpic = (actions$, state$, { history }) =>
  actions$.pipe(
    ofType(authConstants.OTP_CONFIRM_REQUEST),
    mergeMap((action) => {
      return ajax
        .post("/api/auth/otp-confirm", action.payload, getRequestHeader("POST"))
        .pipe(
          tap(() => setSessionActive()),
          concatMap((ajaxResponse) =>
            of(
              regenerateKey(),
              hideCustomeError([responseCodes.AUTH_ERROR_OTP_CONFIRM]),
              getProfileDetailsRequest(),
              otpConfirmComplete(ajaxResponse.response),
              setFirstTimeLoginState(true),
              changeLanguageComplete({
                customerLanguage: ajaxResponse.response.data.customerLanguage,
              }),
              changeLanguageSuccessAlertHide(),
            ),
          ),
          catchError((error) => {
            if (
              error.response?.statusCode == responseCodes.ADMIN_PASSWORD_RESET
            ) {
              setSessionActive();
              return of(
                hideCustomeError([error.response.errorResponse.customer]),
                loginAuthComplete(error.response.errorResponse.customer),
                setAdminPasswordResetStatus(error?.response.errorResponse),
                otpConfirmComplete(error.response),
              );
            }
            if (
              error.response?.statusCode ==
              responseCodes.AUTH_ERROR_PASSWORD_EXPIRED
            ) {
              return of(
                loginAuthComplete(error.response.errorResponse),
                ajaxError(error),
              );
            }

            return of(ajaxError(error));
          }),
        );
    }),
  );

export const otpResendEpic = (actions$) =>
  actions$.pipe(
    ofType(authConstants.OTP_RESEND_REQUEST),
    mergeMap((action) => {
      return ajax
        .post("/api/auth/otp-resend", action.payload, getRequestHeader("POST"))
        .pipe(
          map((ajaxResponse) => otpResendComplete(ajaxResponse.response)),
          catchError((error) => of(ajaxError(error))),
        );
    }),
  );

export const expiredPasswordChangeValueEpic = (actions$) =>
  actions$.pipe(
    ofType(authConstants.EXPIRED_PASSWORD_CHANGE_VALUE_REQUEST),
    mergeMap(async (action) => {
      try {
        const payload = await from(
          payloadFormatter(
            action.payload,
            "/api/auth/expiredPasswordChangeValue",
          ),
        ).toPromise();
        return payloadEncryptionSuccess(payload, action.payload);
      } catch (error) {
        return payloadEncryptionFail(error);
      }
    }),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/auth/expiredPasswordChangeValue",
          action.encryptedPayload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            expiredPasswordChangeValueComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(expiredPasswordChangeValueError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const keyGenerationEpic = (actions$, state$) =>
  actions$.pipe(
    ofType(authConstants.KEY_GENERATION_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/auth/key-generation",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) => {
            return keyGenerationComplete(ajaxResponse.response);
          }),
          catchError((error) => {
            return of(ajaxError(error));
          }),
        );
    }),
  );

export const forgotUsernameValidateEpic = (actions$) =>
  actions$.pipe(
    ofType(authConstants.FORGOT_USERNAME_VALIDATE_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/auth/forgotUsernameValidate",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            forgotUsernameValidateComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(forgotUsernameValidateError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const forgotUsernameOTPConfirmEpic = (actions$) =>
  actions$.pipe(
    ofType(authConstants.FORGOT_USERNAME_OTP_CONFIRM_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/auth/forgotUsernameOTPConfirm",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          concatMap((ajaxResponse) =>
            of(forgotUsernameOTPConfirmComplete(ajaxResponse.response)),
          ),

          catchError((error) =>
            of(forgotUsernameOTPConfirmError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const forgotUsernameOTPResendEpic = (actions$) =>
  actions$.pipe(
    ofType(authConstants.FORGOT_USERNAME_OTP_RESEND_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/auth/forgotUsernameOTPResend",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            forgotUsernameOTPResendComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(forgotUsernameOTPResendError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const csrfTokenGenEpic = (actions$) =>
  actions$.pipe(
    ofType(authConstants.CSRF_TOKEN_GEN_REQUEST),
    mergeMap(() => {
      return ajax.get("/api/auth/csrf-token", getRequestHeader("GET")).pipe(
        map((ajaxResponse) => CSRFTokenGenComplete(ajaxResponse.response)),
        catchError((error) => of(CSRFTokenGenError(error), ajaxError(error))),
      );
    }),
  );

export const changeLanguageEpic = (actions$) =>
  actions$.pipe(
    ofType(authConstants.CHANGE_LANGUAGE_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/auth/change-language",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map(
            (ajaxResponse) =>
              changeLanguageComplete({
                customerLanguage: action.payload.customerLanguage,
              }),
            changeLanguageSuccessAlertHide(),
          ),
          catchError((error) => of(ajaxError(error))),
        );
    }),
  );

export const forgotPasswordValidateEpic = (actions$) =>
  actions$.pipe(
    ofType(authConstants.FORGOT_PASSWORD_VALIDATE_REQUEST),
    mergeMap((action) => {
      const { setModalOpen, ...payload } = action.payload;
      return ajax
        .post(
          "/api/auth/forgotPasswordValidate",
          payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) => {
            setModalOpen?.(true);
            return forgotPasswordValidateComplete(ajaxResponse.response);
          }),
          catchError((error) => {
            if (
              error.response.statusCode ===
              responseCodes.OTP_REQUEST_LIMIT_REACHED
            ) {
              setModalOpen?.(true);
            }

            return of(forgotPasswordValidateError(error), ajaxError(error));
          }),
        );
    }),
  );

export const forgotPasswordOTPConfirmEpic = (actions$) =>
  actions$.pipe(
    ofType(authConstants.FORGOT_PASSWORD_OTP_CONFIRM_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/auth/forgotPasswordOTPConfirm",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          concatMap((ajaxResponse) =>
            of(
              forgotPasswordOTPConfirmComplete(ajaxResponse.response),
              regenerateKey(),
            ),
          ),
          catchError((error) =>
            of(forgotPasswordOTPConfirmError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const forgotPasswordOTPResendEpic = (actions$) =>
  actions$.pipe(
    ofType(authConstants.FORGOT_PASSWORD_OTP_RESEND_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/auth/forgotPasswordOTPResend",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            forgotPasswordOTPResendComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(forgotPasswordOTPResendError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const forgotPasswordAccountConfirmEpic = (actions$) =>
  actions$.pipe(
    ofType(authConstants.FORGOT_PASSWORD_ACCOUNT_CONFIRM_REQUEST),
    mergeMap(async (action) => {
      try {
        const payload = await from(
          payloadFormatter(
            action.payload,
            "/api/auth/forgotPasswordAccountConfirm",
          ),
        ).toPromise();
        return payloadEncryptionSuccess(payload, action.payload);
      } catch (error) {
        return payloadEncryptionFail(error);
      }
    }),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/auth/forgotPasswordAccountConfirm",
          action.encryptedPayload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            forgotPasswordAccountConfirmComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(forgotPasswordAccountConfirmError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const forgotPasswordChangeValueEpic = (actions$) =>
  actions$.pipe(
    ofType(authConstants.FORGOT_PASSWORD_CHANGE_VALUE_REQUEST),
    mergeMap(async (action) => {
      try {
        const payload = await from(
          payloadFormatter(
            action.payload,
            "/api/auth/forgotPasswordChangeValue",
          ),
        ).toPromise();
        return payloadEncryptionSuccess(payload, action.payload);
      } catch (error) {
        return payloadEncryptionFail(error);
      }
    }),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/auth/forgotPasswordChangeValue",
          action.encryptedPayload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            forgotPasswordChangeValueComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(forgotPasswordChangeValueError(error), ajaxError(error)),
          ),
        );
    }),
  );
